// api/user-preferences.api.ts
import axiosInstance from './axios-instance';

export interface WeightsDto {
  educationalValueWeight: number;
  lightheartednessWeight: number;
  contentQualityWeight: number;
  contentTrustworthinessWeight: number;
  clickbaitWeight: number;
  integrityWeight: number;
  civilityWeight: number;
  recencyWeight: number;
  perspectiveDiversityWeight: number;
  originalityWeight: number;
  sentimentWeight: number;
  embeddingSimilarityWeight: number;
}

export interface CreatePreferenceDto {
  name?: string;
  description?: string;
  instruction?: string;
  weights: WeightsDto;
}

export interface UserFeedPreference {
  id: string;
  userId?: string;
  name: string;
  description?: string;
  sentimentWeight: number;
  contentQualityWeight: number;
  contentTrustworthinessWeight: number;
  clickbaitWeight: number;
  integrityWeight: number;
  civilityWeight: number;
  educationalValueWeight: number;
  lightheartednessWeight: number;
  perspectiveDiversityWeight: number;
  originalityWeight: number;
  recencyWeight: number;
  embeddingSimilarityWeight: number;
  createdAt: Date;
  updatedAt: Date;
  _isCustom?: boolean;
}

export interface EnrichInstructionDto {
  instruction: string;
}

export interface EnrichInstructionResponse {
  originalInstruction: string;
  enrichedInstruction: string;
  wasEnriched: boolean;
}

export interface InstructionComponents {
  originalInstruction: string;
  topics: string[];
  tones: string[];
  suggestedWeights: Record<string, number>;
  topicForEmbedding: string;
  explanation: {
    topics: string;
    tones: string;
    suggestedWeights: string;
    topicForEmbedding: string;
  };
}

export interface PromptValidation {
  originalInstruction: string;
  quality: 'excellent' | 'good' | 'needs_improvement';
  score: number;
  issues: string[];
  suggestions: string[];
  reasoning: string;
  canProceed: boolean;
  recommendations: {
    shouldEnhance: boolean;
    shouldRevise: boolean;
    isReady: boolean;
  };
}

export const userPreferencesApi = {
  // Get all default presets
  getDefaultPresets: async (): Promise<UserFeedPreference[]> => {
    const { data } = await axiosInstance.get('/api/v1/publisher/user-preferences/defaults');
    return data;
  },

  // Get user's custom presets
  getUserPresets: async (): Promise<UserFeedPreference[]> => {
    const { data } = await axiosInstance.get('/api/v1/publisher/user-preferences/user');
    return data;
  },

  // Get the current active preset
  getActivePreset: async (): Promise<UserFeedPreference> => {
    const { data } = await axiosInstance.get('/api/v1/publisher/user-preferences/active');
    return data;
  },

  // Update weights on the active preset
  updateActiveWeights: async (weights: WeightsDto): Promise<{ message: string }> => {
    const { data } = await axiosInstance.put('/api/v1/publisher/user-preferences/active/weights', {
      weights
    });
    return data;
  },

  // Update weights on a specific preset
  updatePresetWeights: async (
    presetId: string,
    weights: WeightsDto
  ): Promise<{ message: string }> => {
    const { data } = await axiosInstance.put(
      `/api/v1/publisher/user-preferences/preset/${presetId}/weights`,
      {
        weights
      }
    );
    return data;
  },

  // Switch active preset to a default one
  setActiveFromDefault: async (id: string): Promise<{ message: string }> => {
    const { data } = await axiosInstance.put(
      `/api/v1/publisher/user-preferences/active/default/${id}`
    );
    return data;
  },

  // Switch active preset to one of your own presets
  setActiveFromUserPreset: async (id: string): Promise<{ message: string }> => {
    const { data } = await axiosInstance.put(
      `/api/v1/publisher/user-preferences/active/user/${id}`
    );
    return data;
  },

  // Create a new user preset
  createUserPreset: async (preset: CreatePreferenceDto): Promise<UserFeedPreference> => {
    const { data } = await axiosInstance.post('/api/v1/publisher/user-preferences/user', preset);
    return data;
  },

  // Create a new user preset with component-based processing (respects manual weights)
  createUserPresetWithComponents: async (
    preset: CreatePreferenceDto
  ): Promise<UserFeedPreference> => {
    const { data } = await axiosInstance.post(
      '/api/v1/publisher/user-preferences/user/with-components',
      preset
    );
    return data;
  },

  // Save current active preset as a new custom preset
  saveCurrentAsPreset: async (
    name: string,
    description?: string,
    instruction?: string
  ): Promise<UserFeedPreference> => {
    const { data } = await axiosInstance.post(
      '/api/v1/publisher/user-preferences/user/save-current',
      {
        name,
        description,
        instruction
      }
    );
    return data;
  },

  // Delete a user preset
  deleteUserPreset: async (id: string): Promise<{ message: string }> => {
    const { data } = await axiosInstance.delete(`/api/v1/publisher/user-preferences/user/${id}`);
    return data;
  },

  // Update a preset's name
  updatePresetName: async (presetId: string, name: string): Promise<UserFeedPreference> => {
    const { data } = await axiosInstance.put(
      `/api/v1/publisher/user-preferences/user/${presetId}/name`,
      {
        name
      }
    );
    return data;
  },

  // Enrich a user instruction to make it more detailed and specific
  enrichInstruction: async (instruction: string): Promise<EnrichInstructionResponse> => {
    const { data } = await axiosInstance.post(
      '/api/v1/publisher/user-preferences/enrich-instruction',
      {
        instruction
      }
    );
    return data;
  },

  // Parse instruction into components (topics, tones, weights)
  parseInstructionComponents: async (instruction: string): Promise<InstructionComponents> => {
    const response = await axiosInstance.post(
      '/api/v1/publisher/user-preferences/parse-components',
      {
        instruction
      }
    );
    console.log('parseInstructionComponents response after interceptor:', response);

    if (!response.success) {
      throw new Error(`API error: ${response.message || 'Unknown error'}`);
    }

    if (!response.data) {
      throw new Error('No data in parseInstructionComponents response');
    }

    return response.data; // Axios interceptor already extracted response.data
  },

  // Validate prompt quality and get improvement suggestions
  validatePromptQuality: async (instruction: string): Promise<PromptValidation> => {
    const response = await axiosInstance.post(
      '/api/v1/publisher/user-preferences/validate-prompt',
      {
        instruction
      }
    );
    console.log('validatePromptQuality response after interceptor:', response);

    if (!response.success) {
      throw new Error(`API error: ${response.message || 'Unknown error'}`);
    }

    if (!response.data) {
      throw new Error('No data in API response');
    }

    return response.data; // Axios interceptor already extracted response.data
  }
};
